import React, { useState, useEffect, useCallback } from 'react';
// Removed: import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { ChevronLeft, ChevronRight, RotateCcw, X, Check, HelpCircle, Award, ListChecks, Users, Play } from 'lucide-react';
import MONIKERS_CARDS_DESI_INITIAL from './cardData'; // Import the card data

// Card Data (Full list of 200 cards - remains unchanged)
// const MONIKERS_CARDS_INDIA_INITIAL = [ ... ] // This large array is now in cardData.js

// Helper function to shuffle an array
const shuffleArray = (array) => {
  return [...array].sort(() => Math.random() - 0.5);
};

// Helper function to select a random subset of cards
const selectRandomSubset = (cardsArray, count) => {
  return shuffleArray(cardsArray).slice(0, count);
};

// --- Inline Styles Definitions ---
// It's often better to define style objects outside the component
// to prevent re-creation on every render, or use useMemo.

const commonStyles = {
  button: {
    color: 'white',
    fontWeight: 600,
    borderRadius: '0.5rem', // rounded-lg or rounded-xl
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    transition: 'transform 0.1s, background-color 0.2s',
    transform: 'scale(1)',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconMargin: {
    marginRight: '0.5rem'
  }
};

// --- App Component ---
function DesiMonikers() {
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);

  // Game Setup State
  const [gamePhase, setGamePhase] = useState('setup'); // 'setup', 'playing', 'gameOver'
  const [team1Name, setTeam1Name] = useState('Team 1');
  const [team2Name, setTeam2Name] = useState('Team 2');
  // const [numPlayers, setNumPlayers] = useState(4); // Example, can be made dynamic

  const CARDS_PER_GAME = 40; // Define the constant here

  // Game state
  const [gameCards, setGameCards] = useState(() => selectRandomSubset(MONIKERS_CARDS_DESI_INITIAL, CARDS_PER_GAME));
  const [deck, setDeck] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [roundGuessedPile, setRoundGuessedPile] = useState([]);

  // Team and Score state
  const [team1Score, setTeam1Score] = useState(0);
  const [team2Score, setTeam2Score] = useState(0);
  const [currentTeam, setCurrentTeam] = useState(1); // 1 for Team 1, 2 for Team 2
  // const [currentPlayer, setCurrentPlayer] = useState(1); // Can be expanded for individual player turns within a team

  const [round, setRound] = useState(1);
  
  const [round1GuessedCards, setRound1GuessedCards] = useState([]); // Cards guessed in R1 by ANY team
  const [round2GuessedCards, setRound2GuessedCards] = useState([]); // Cards guessed in R2 by ANY team

  // UI/Animation state
  const [animationClass, setAnimationClass] = useState('');
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchCurrentX, setTouchCurrentX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isRoundOver, setIsRoundOver] = useState(false); // Indicates if all cards in current round's deck are played
  const [isTurnOver, setIsTurnOver] = useState(false); // Indicates if current team's 60s turn is over
  const [showHelp, setShowHelp] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [timer, setTimer] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const [isGameStarting, setIsGameStarting] = useState(false); // New state for initial game start screen

  // Button hover state
  const [passHover, setPassHover] = useState(false);
  const [correctHover, setCorrectHover] = useState(false);
  const [nextRoundHover, setNextRoundHover] = useState(false); // For round end screen
  const [nextTurnHover, setNextTurnHover] = useState(false); // For turn end screen
  const [restartHover, setRestartHover] = useState(false);
  const [helpButtonHover, setHelpButtonHover] = useState(false);
  const [rulesButtonHover, setRulesButtonHover] = useState(false);
  const [startGameHover, setStartGameHover] = useState(false);


  // Effect for 60-second turn timer
  useEffect(() => {
    let intervalId;
    if (timerActive && timer > 0) {
      intervalId = setInterval(() => {
        setTimer(prevTimer => prevTimer - 1);
      }, 1000);
    } else if (timerActive && timer === 0) {
      setTimerActive(false);
      setIsTurnOver(true); // Mark turn as over
      // Don't automatically switch team here, let user click "Next Turn"
    }
    return () => clearInterval(intervalId);
  }, [timerActive, timer]);

  // Effect to setup deck for the current round
  useEffect(() => {
    if (gamePhase !== 'playing') return;

    // For all rounds, the source of cards will be the initially selected 'gameCards'.
    // These are shuffled again for each new round.
    const currentDeckSource = [...gameCards];
    
    setDeck(shuffleArray(currentDeckSource)); // Shuffle the 20 cards for the current round
    setCurrentIndex(0);
    setRoundGuessedPile([]); // Reset for the new round (this is for cards guessed IN THIS ROUND)
    setIsRoundOver(false); // Round is not over at the start
    setIsTurnOver(false);  // Turn is not over
    setTimer(60);          // Reset timer for the first turn of the round
    // setTimerActive(true); // Start timer for the first turn, or manage this via a "Start Turn" button
    setAnimationClass('animate-enter-active');
  }, [round, gameCards, gamePhase]); // Updated dependencies

  const currentCard = deck[currentIndex];
  const cardsRemainingInDeck = deck.length - currentIndex; // Cards left in the *overall* deck for this round

  const handleNextCard = useCallback((action) => {
    if (!currentCard || isTurnOver || isRoundOver || deck.length === 0) return;
    let swipeAnimationClass = '';
    if (action === 'correct') swipeAnimationClass = 'animate-swipe-right';
    else if (action === 'pass') swipeAnimationClass = 'animate-swipe-left';
    setAnimationClass(swipeAnimationClass);

    setTimeout(() => {
      if (action === 'correct') {
        setRoundGuessedPile(prev => [...prev, currentCard]);
        if (currentTeam === 1) {
          setTeam1Score(prevScore => prevScore + currentCard.points);
        } else {
          setTeam2Score(prevScore => prevScore + currentCard.points);
        }

        const newDeck = deck.filter(card => card.name !== currentCard.name);
        setDeck(newDeck);

        if (newDeck.length === 0) {
          setIsRoundOver(true);
          setTimerActive(false);
          setAnimationClass('animate-game-over');
        } else {
          setCurrentIndex(prevIndex => Math.min(prevIndex, newDeck.length - 1));
          setAnimationClass('animate-enter');
          requestAnimationFrame(() => setAnimationClass('animate-enter-active'));
        }
      } else { // 'pass'
        if (deck.length <= 1) {
          // Cannot pass if only one card or no card, reset animation
          setAnimationClass('animate-snap-back');
          requestAnimationFrame(() => setTimeout(() => setAnimationClass('animate-enter-active'), 0));
          return;
        }
        const cardToPass = currentCard;
        // Create new deck with the passed card moved to the end
        const newDeck = deck.filter(card => card.name !== cardToPass.name);
        newDeck.push(cardToPass);
        setDeck(newDeck);
        // currentIndex does not change. The card at deck[currentIndex] is now the next one.
        // Re-trigger animation for the new card at current index.
        setAnimationClass('animate-enter');
        requestAnimationFrame(() => setAnimationClass('animate-enter-active'));
      }
    }, 300);
  }, [currentCard, deck, currentIndex, currentTeam, isTurnOver, isRoundOver]); // Removed gameCards.length, roundGuessedPile.length and added deck


  const handleTouchStart = (e) => {
    if (!currentCard || isRoundOver || isTurnOver) return;
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchCurrentX(e.targetTouches[0].clientX);
    setIsSwiping(true);
    setAnimationClass('');
  };

  const handleTouchMove = (e) => {
    if (!isSwiping || !currentCard || isRoundOver || isTurnOver) return;
    const currentX = e.targetTouches[0].clientX;
    setTouchCurrentX(currentX);
    const diffX = currentX - touchStartX;
    const cardElement = e.currentTarget;
    cardElement.style.transform = `translateX(${diffX}px) rotate(${diffX / 20}deg)`;
    cardElement.style.opacity = `${1 - Math.abs(diffX) / (cardElement.offsetWidth / 1.5)}`;
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping || !currentCard || isRoundOver || isTurnOver) return;
    setIsSwiping(false);
    const cardElement = e.currentTarget;
    const diffX = touchCurrentX - touchStartX;
    const threshold = cardElement.offsetWidth / 3.5;

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) handleNextCard('correct');
      else handleNextCard('pass');
    } else {
      setAnimationClass('animate-snap-back');
      requestAnimationFrame(() => setTimeout(() => setAnimationClass('animate-enter-active'), 0));
    }
    cardElement.style.transform = '';
    cardElement.style.opacity = '';
    setTouchStartX(0);
    setTouchCurrentX(0);
  };

  const startNextTurn = () => {
    // Reshuffle all cards currently in the deck (passed cards are at the end, guessed cards are removed)
    setDeck(shuffleArray([...deck]));
    setCurrentIndex(0);
    setCurrentTeam(prevTeam => (prevTeam === 1 ? 2 : 1)); // Switch team
    setTimer(60);
    setTimerActive(true);
    setIsTurnOver(false);
    setAnimationClass('animate-enter-active'); // Animation for new card
  };


  const advanceToNextRound = () => {
    // The check for roundGuessedPile.length === 0 to end the game is removed.
    // The game proceeds to the next round using the full initial set of 20 cards.

    if (round === 1) {
      setRound1GuessedCards([...roundGuessedPile]); // Save all cards guessed in R1 (for record keeping)
    } else if (round === 2) {
      setRound2GuessedCards([...roundGuessedPile]); // Save all cards guessed in R2 (for record keeping)
    }

    if (round < 3) {
      setRound(prev => prev + 1); // This will trigger the useEffect for round setup
      // The useEffect will also reset setIsRoundOver, setTimer, etc.
      setCurrentTeam(team1Score <= team2Score ? 1 : 2); // Team with lower score starts next round
      setTimerActive(true); // Start timer for the new round's first turn
    } else { // After round 3
      setGamePhase('gameOver');
      // alert(`Game Over! Final Score: ${team1Name}: ${team1Score}, ${team2Name}: ${team2Score}. Thanks for playing!`);
      // No need to call restartGame() here, gameOver phase will handle it
    }
    // Resetting round-specific states is handled by the round setup useEffect
    setIsRoundOver(false); // Ensure this is reset for the new round start
    setIsTurnOver(false);
  };


  const restartGame = () => {
    setGamePhase('setup'); // Go back to setup screen
    setTeam1Score(0);
    setTeam2Score(0);
    setRound(1);
    setGameCards(selectRandomSubset(MONIKERS_CARDS_DESI_INITIAL, CARDS_PER_GAME)); // Reshuffle and select new subset
    setRound1GuessedCards([]);
    setRound2GuessedCards([]);
    setRoundGuessedPile([]);
    setCurrentIndex(0);
    setCurrentTeam(1);
    setIsRoundOver(false);
    setIsTurnOver(false);
    setShowHelp(false);
    setShowRules(false);
    setTimer(60);
    setTimerActive(false);
    setIsGameStarting(false); // Reset isGameStarting state
  };
  
  const handleStartGame = () => {
    if (team1Name.trim() === '' || team2Name.trim() === '') {
      alert("Please enter names for both teams.");
      return;
    }
    setGamePhase('playing');
    setIsGameStarting(true); // Show the "Start First Turn" screen
    // Timer will now be started by the button on the isGameStarting screen
    // Round setup useEffect will be triggered by gamePhase change to 'playing'
  };

  const handleBeginFirstTurn = () => {
    setIsGameStarting(false);
    setTimerActive(true);
  };

  // Styles that depend on state (like isDesktop or hover)
  const appContainerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #4f46e5, #7c3aed, #ec4899)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    fontFamily: "'Inter', sans-serif",
    color: '#1f2937',
  };

  const headerContainerStyle = {
    width: '100%',
    maxWidth: '28rem',
    marginBottom: isDesktop ? '1.5rem' : '1rem',
    color: 'white',
  };
  
  const appTitleStyle = {
      fontSize: isDesktop ? '2.75rem' : '2.25rem',
      lineHeight: isDesktop ? '3rem' : '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.025em',
      marginRight: 'auto',
      color: 'white',
      textShadow: '0 2px 4px rgba(0,0,0,0.2)',
  };

  const helpButtonStyle = {
    padding: '0.5rem',
    borderRadius: '9999px',
    transition: 'background-color 0.2s',
    backgroundColor: helpButtonHover ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    marginLeft: '0.5rem', // Space from rules button
  };
  
  const helpTextContainerStyle = {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      padding: '0.75rem',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      marginBottom: '0.75rem',
      // animation: 'enterAnimation 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)', // Applied by className
      WebkitTapHighlightColor: 'transparent',
  };

  const mainGameAreaStyle = {
    width: '100%',
    maxWidth: '28rem',
    height: isDesktop ? '24rem' : '20rem',
    marginBottom: isDesktop ? '1.5rem' : '1rem',
  };

  const cardElementBaseStyle = {
    width: '100%',
    height: '100%',
    userSelect: 'none',
    cursor: 'grab',
    background: 'linear-gradient(to bottom right, white, #f9fafb)',
    borderRadius: '0.75rem',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    padding: isDesktop ? '2rem' : '1.5rem',
    transition: 'transform 0.3s cubic-bezier(0.215, 0.610, 0.355, 1), opacity 0.3s cubic-bezier(0.215, 0.610, 0.355, 1)',
    transformOrigin: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    perspective: '1000px',
    WebkitTapHighlightColor: 'transparent',
  };
  
  const cardNameStyle = {
      fontSize: isDesktop ? '1.875rem' : '1.5rem',
      lineHeight: isDesktop ? '2.25rem' : '2rem',
      fontWeight: 700,
      color: '#4338ca',
      wordBreak: 'break-word',
      width: '100%',
      paddingRight: '0.5rem',
  };

  const cardPointsStyle = {
      flexShrink: 0,
      fontSize: isDesktop ? '1.25rem' : '1.125rem',
      lineHeight: '1.75rem',
      fontWeight: 600,
      color: '#d97706',
      backgroundColor: '#fef3c7',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
  };
  
  const clueContainerStyle = {
      backgroundColor: '#eef2ff',
      padding: isDesktop ? '1rem' : '0.75rem',
      borderRadius: '0.5rem',
      minHeight: isDesktop ? '100px' : '80px',
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
  };

  const clueTextStyle = {
      color: '#374151',
      fontSize: isDesktop ? '1rem' : '0.875rem',
      lineHeight: isDesktop ? '1.5rem' : '1.25rem',
      textAlign: 'center',
      minHeight: '40px', // Ensure space for round instructions
  };

  const actionButtonStyle = (type, isHovering) => ({
    ...commonStyles.button,
    backgroundColor: type === 'danger' ? (isHovering ? '#dc2626' : '#ef4444') : (type === 'primary' ? (isHovering ? '#4338ca' : '#4f46e5') : (isHovering ? '#059669' : '#10b981')),
    padding: type === 'primary' ? '0.75rem 1.5rem' : '1rem 1rem',
    fontSize: type === 'primary' ? '1rem' : '1.125rem',
    transform: isHovering ? 'scale(1.05)' : 'scale(1)',
  });

  const nextTurnButtonStyle = (isHovering) => ({
    ...commonStyles.button,
    backgroundColor: isHovering ? '#1d4ed8' : '#2563eb', // blue-700, blue-600
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    marginTop: '1rem',
    transform: isHovering ? 'scale(1.05)' : 'scale(1)',
  });

  const restartButtonStyle = {
    ...commonStyles.button,
    marginTop: '2rem',
    backgroundColor: restartHover ? '#1f2937' : '#374151',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    transform: restartHover ? 'scale(1.05)' : 'scale(1)',
  };

  const rulesButtonStyle = {
    ...commonStyles.button,
    backgroundColor: rulesButtonHover ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.15)',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  };

  const rulesModalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  };

  const rulesModalContentStyle = {
    background: 'white',
    padding: isDesktop ? '2rem' : '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    width: '100%',
    maxWidth: '36rem',
    maxHeight: '90vh',
    overflowY: 'auto',
    color: '#374151',
  };

  const globalStyleSheet = `
    body {
      margin: 0;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      color: #374151; /* Default text color for body */
    }
    * {
      box-sizing: border-box;
    }
    .animate-enter { opacity: 0; transform: scale(0.8) translateY(20px); }
    .animate-enter-active { opacity: 1; transform: scale(1) translateY(0); transition: transform 0.3s cubic-bezier(0.215, 0.610, 0.355, 1), opacity 0.3s cubic-bezier(0.215, 0.610, 0.355, 1); }
    .animate-swipe-left { transform: translateX(-120%) rotate(-12deg) !important; opacity: 0 !important; transition: transform 0.3s ease-out, opacity 0.3s ease-out; }
    .animate-swipe-right { transform: translateX(120%) rotate(12deg) !important; opacity: 0 !important; transition: transform 0.3s ease-out, opacity 0.3s ease-out; }
    .animate-game-over { opacity: 0.7; transform: scale(0.95); }
    .animate-snap-back { transform: translateX(0) rotate(0); opacity: 1; transition: transform 0.2s ease-out, opacity 0.2s ease-out; }
  `;

  // ----- SETUP SCREEN -----
  if (gamePhase === 'setup') {
    const setupContainerStyle = {
      width: '100%',
      maxWidth: '28rem', // sm:max-w-md
      padding: isDesktop ? '2rem' : '1.5rem', // sm:p-8
      backgroundColor: 'white',
      borderRadius: '0.75rem', // rounded-xl
      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', // shadow-2xl
      textAlign: 'center',
    };
    const setupTitleStyle = {
      fontSize: isDesktop ? '1.875rem' : '1.5rem', // text-2xl sm:text-3xl
      fontWeight: 700, // font-bold
      color: '#4f46e5', // text-indigo-600
      marginBottom: '1.5rem', // mb-6
    };
    const inputGroupStyle = {
      marginBottom: '1rem', // mb-4
      textAlign: 'left',
    };
    const labelStyle = {
      display: 'block',
      fontSize: '0.875rem', // text-sm
      fontWeight: 500, // font-medium
      color: '#374151', // text-gray-700
      marginBottom: '0.25rem', // mb-1
    };
    const inputStyle = {
      width: '100%',
      padding: '0.5rem 0.75rem', // py-2 px-3
      border: '1px solid #d1d5db', // border-gray-300
      borderRadius: '0.375rem', // rounded-md
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)', // shadow-sm
      fontSize: '1rem', // text-base
    };
     const startButtonStyle = {
      ...commonStyles.button,
      backgroundColor: startGameHover ? '#059669' : '#10b981', // hover:bg-emerald-600 bg-emerald-500
      padding: '0.75rem 2rem', // py-3 px-8
      fontSize: '1.125rem', // text-lg
      width: '100%',
      marginTop: '1rem', // mt-4
      transform: startGameHover ? 'scale(1.02)' : 'scale(1)',
    };

    return (
      <>
        <style>{globalStyleSheet}</style>
        <div style={appContainerStyle}>
          <div style={setupContainerStyle}>
            <Users size={isDesktop ? 48 : 40} style={{ color: '#4f46e5', margin: '0 auto 1rem auto' }} />
            <h1 style={setupTitleStyle}>Game Setup</h1>
            
            <div style={inputGroupStyle}>
              <label htmlFor="team1Name" style={labelStyle}>Team 1 Name:</label>
              <input 
                type="text" 
                id="team1Name"
                style={inputStyle}
                value={team1Name} 
                onChange={(e) => setTeam1Name(e.target.value)} 
                placeholder="Enter Team 1 Name"
              />
            </div>

            <div style={inputGroupStyle}>
              <label htmlFor="team2Name" style={labelStyle}>Team 2 Name:</label>
              <input 
                type="text" 
                id="team2Name"
                style={inputStyle}
                value={team2Name} 
                onChange={(e) => setTeam2Name(e.target.value)}
                placeholder="Enter Team 2 Name" 
              />
            </div>
            {/* Placeholder for number of players if needed later
            <div style={inputGroupStyle}>
              <label htmlFor="numPlayers" style={labelStyle}>Number of Players (e.g., 4, 6, 8):</label>
              <input type="number" id="numPlayers" style={inputStyle} value={numPlayers} onChange={(e) => setNumPlayers(parseInt(e.target.value,10) || 2)} min="2" />
            </div>
            */}
            <button
              style={startButtonStyle}
              onClick={handleStartGame}
              onMouseEnter={() => setStartGameHover(true)}
              onMouseLeave={() => setStartGameHover(false)}
            >
              <Play size={20} style={{...commonStyles.iconMargin, marginRight: '0.75rem'}} /> Start Game
            </button>
          </div>
        </div>
      </>
    );
  }

  // ----- GAME OVER SCREEN -----
  if (gamePhase === 'gameOver') {
    const winner = team1Score > team2Score ? team1Name : (team2Score > team1Score ? team2Name : "It's a Tie!");
    const gameOverCardStyle = {
      ...cardElementBaseStyle, 
      alignItems: 'center', 
      justifyContent: 'center', 
      textAlign: 'center', 
      backgroundColor: 'white'
    };
  return (
    <>
      <style>{globalStyleSheet}</style>
      <div style={appContainerStyle}>
        <header style={headerContainerStyle}>
            <h1 style={{...appTitleStyle, textAlign: 'center', width: '100%', marginBottom: '1rem'}}>Game Over!</h1>
          </header>
          <div style={gameOverCardStyle} className="animate-enter-active">
            <Award size={isDesktop ? 64 : 48} style={{ color: '#4f46e5', marginBottom: '1rem' }} />
            <h2 style={{ fontSize: isDesktop ? '1.75rem' : '1.375rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem' }}>
              {winner === "It's a Tie!" ? "It's a Tie!" : `${winner} Wins!`}
            </h2>
            <p style={{ fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4b5563', marginBottom: '0.25rem' }}>{team1Name}: {team1Score}</p>
            <p style={{ fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4b5563', marginBottom: '1.5rem' }}>{team2Name}: {team2Score}</p>
            <button
              style={actionButtonStyle('primary', restartHover)} // Re-using primary style
              onClick={restartGame}
              onMouseEnter={() => setRestartHover(true)}
              onMouseLeave={() => setRestartHover(false)}
            >
              <RotateCcw size={CARDS_PER_GAME} style={commonStyles.iconMargin} /> Play Again?
            </button>
          </div>
        </div>
      </>
    );
  }


  // ----- MAIN GAME PLAYING SCREEN -----
  if (gamePhase === 'playing') {
    if (isGameStarting) {
      return (
        <>
          <style>{globalStyleSheet}</style>
          <div style={appContainerStyle}>
             <header style={headerContainerStyle}>
              {/* Simplified header for this screen, or could be full header */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h1 style={{...appTitleStyle, marginRight: 0}}>Desi Monikers</h1>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.25)', padding: '0.75rem', borderRadius: '0.5rem' }}>
                <div style={{ textAlign: 'center', color: 'white' }}>{team1Name}</div>
                <div style={{ textAlign: 'center', color: 'white', fontSize: '1.25rem' }}>VS</div>
                <div style={{ textAlign: 'center', color: 'white' }}>{team2Name}</div>
              </div>
            </header>
            <main style={mainGameAreaStyle}>
              <div style={{ ...cardElementBaseStyle, alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: 'white' }} className="animate-enter-active">
                <Play size={isDesktop ? 54 : 40} style={{ color: '#4f46e5', marginBottom: '1rem' }} />
                <h2 style={{ fontSize: isDesktop ? '1.5rem' : '1.25rem', fontWeight: 700, color: '#374151' }}>Ready?</h2>
                <p style={{ color: '#4b5563', margin: '0.5rem 0 1rem 0', fontSize: isDesktop ? '1rem' : '0.875rem' }}>
                  {currentTeam === 1 ? team1Name : team2Name} starts Round {round}.
                </p>
                <button
                  style={nextTurnButtonStyle(startGameHover)} // Can reuse nextTurnButtonStyle or make a specific one
                  onClick={handleBeginFirstTurn}
                  onMouseEnter={() => setStartGameHover(true)} 
                  onMouseLeave={() => setStartGameHover(false)}
                >
                  Start First Turn
                </button>
              </div>
            </main>
             <button 
                style={restartButtonStyle} 
                onClick={restartGame} 
                aria-label="Restart Game"
                onMouseEnter={() => setRestartHover(true)}
                onMouseLeave={() => setRestartHover(false)}
              >
                <RotateCcw size={18} style={commonStyles.iconMargin} /> Restart Game
            </button>
          </div>
        </>
      );
    } else if (isRoundOver) {
      // Existing isRoundOver screen logic
      return (
        <>
          <style>{globalStyleSheet}</style>
          <div style={appContainerStyle}>
            <header style={headerContainerStyle}>
              {/* Full header as in main play */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h1 style={appTitleStyle}>Desi Monikers</h1>
                <div>
                  <button style={rulesButtonStyle} onClick={() => setShowRules(true)} onMouseEnter={() => setRulesButtonHover(true)} onMouseLeave={() => setRulesButtonHover(false)} aria-label="Show Game Rules">Rules</button>
                  <button style={helpButtonStyle} onClick={() => setShowHelp(!showHelp)} onMouseEnter={() => setHelpButtonHover(true)} onMouseLeave={() => setHelpButtonHover(false)} aria-label="Show Quick Help"><HelpCircle size={24} /></button>
                </div>
              </div>
              {showHelp && (
                <div style={helpTextContainerStyle} className={showHelp ? "animate-enter-active" : ""}>
                  <p><strong>Round 1:</strong> Describe freely (no name parts!).</p>
                  <p><strong>Round 2:</strong> Use only ONE word.</p>
                  <p><strong>Round 3:</strong> Charades only (no words).</p>
                  <p style={{marginTop: '0.25rem'}}>Swipe right or click ✓ for CORRECT. Swipe left or click X to PASS.</p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.25)', padding: '0.75rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                <div style={{ textAlign: 'center', color: currentTeam === 1 ? '#ec4899': 'white', fontWeight: currentTeam === 1 ? 700 : 500 }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team1Name}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team1Score}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Round {round}/3</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>Round Over</div>
                </div>
                <div style={{ textAlign: 'center', color: currentTeam === 2 ? '#ec4899': 'white', fontWeight: currentTeam === 2 ? 700 : 500 }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team2Name}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team2Score}</div>
                </div>
              </div>
            </header>
            <main style={mainGameAreaStyle}>
              <div style={{ ...cardElementBaseStyle, alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: 'white' }} className="animate-enter-active">
                <Award size={isDesktop ? 64 : 48} style={{ color: '#4f46e5', marginBottom: '1rem' }} />
                <h2 style={{ fontSize: isDesktop ? '1.5rem' : '1.25rem', fontWeight: 700, color: '#374151' }}>Round {round} Over!</h2>
                <p style={{ color: '#4b5563', margin: '0.5rem 0', fontSize: isDesktop ? '1rem' : '0.875rem' }}>Total cards guessed this round: {roundGuessedPile.length}</p>
                <p style={{ fontSize: '1rem', color: '#1f2937', fontWeight: 600, marginTop: '0.5rem' }}>Scores:</p>
                <p style={{ fontSize: '0.9rem', color: '#374151' }}>{team1Name}: {team1Score}</p>
                <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '1rem' }}>{team2Name}: {team2Score}</p>
                <button
                  style={{...actionButtonStyle('primary', nextRoundHover), marginTop: '1rem'}}
                  onClick={advanceToNextRound}
                  onMouseEnter={() => setNextRoundHover(true)}
                  onMouseLeave={() => setNextRoundHover(false)}
                >
                  {round < 3 ? `Start Round ${round + 1}` : "Show Final Results"}
                </button>
              </div>
            </main>
            <button 
                style={restartButtonStyle} 
                onClick={restartGame} 
                aria-label="Restart Game"
                onMouseEnter={() => setRestartHover(true)}
                onMouseLeave={() => setRestartHover(false)}
              >
                <RotateCcw size={18} style={commonStyles.iconMargin} /> Restart Game
            </button>
          </div>
        </>
      );
    } else if (isTurnOver) {
       // Existing isTurnOver screen logic
       return (
        <>
          <style>{globalStyleSheet}</style>
          <div style={appContainerStyle}>
            <header style={headerContainerStyle}>
              {/* Full header as in main play */}
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h1 style={appTitleStyle}>Desi Monikers</h1>
                <div>
                  <button style={rulesButtonStyle} onClick={() => setShowRules(true)} onMouseEnter={() => setRulesButtonHover(true)} onMouseLeave={() => setRulesButtonHover(false)} aria-label="Show Game Rules">Rules</button>
                  <button style={helpButtonStyle} onClick={() => setShowHelp(!showHelp)} onMouseEnter={() => setHelpButtonHover(true)} onMouseLeave={() => setHelpButtonHover(false)} aria-label="Show Quick Help"><HelpCircle size={24} /></button>
                </div>
              </div>
              {showHelp && (
                <div style={helpTextContainerStyle} className={showHelp ? "animate-enter-active" : ""}>
                  <p><strong>Round 1:</strong> Describe freely (no name parts!).</p>
                  <p><strong>Round 2:</strong> Use only ONE word.</p>
                  <p><strong>Round 3:</strong> Charades only (no words).</p>
                  <p style={{marginTop: '0.25rem'}}>Swipe right or click ✓ for CORRECT. Swipe left or click X to PASS.</p>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.25)', padding: '0.75rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                 <div style={{ textAlign: 'center', color: currentTeam === 1 ? '#ec4899': 'white', fontWeight: currentTeam === 1 ? 700 : 500 }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team1Name}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team1Score}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Round {round}/3</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>Time Up!</div>
                </div>
                <div style={{ textAlign: 'center', color: currentTeam === 2 ? '#ec4899': 'white', fontWeight: currentTeam === 2 ? 700 : 500 }}>
                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team2Name}</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team2Score}</div>
                </div>
              </div>
               <div style={{marginTop: '0.5rem', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.875rem'}}>
                 {(currentTeam === 1 ? team2Name : team1Name)}'s Turn Next ({deck.length} cards left in round)
               </div>
            </header>
            <main style={mainGameAreaStyle}>
              <div style={{ ...cardElementBaseStyle, alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: 'white' }} className="animate-enter-active">
                <RotateCcw size={isDesktop ? 54 : 40} style={{ color: '#4f46e5', marginBottom: '1rem' }} />
                <h2 style={{ fontSize: isDesktop ? '1.5rem' : '1.25rem', fontWeight: 700, color: '#374151' }}>Turn Over!</h2>
                <p style={{ color: '#4b5563', margin: '0.5rem 0 1rem 0', fontSize: isDesktop ? '1rem' : '0.875rem' }}>
                  Next up: {currentTeam === 1 ? team2Name : team1Name}
                </p>
                <button
                  style={nextTurnButtonStyle(nextTurnHover)}
                  onClick={startNextTurn}
                  onMouseEnter={() => setNextTurnHover(true)}
                  onMouseLeave={() => setNextTurnHover(false)}
                >
                  Start Next Turn
                </button>
              </div>
            </main>
            <button 
                style={restartButtonStyle} 
                onClick={restartGame} 
                aria-label="Restart Game"
                onMouseEnter={() => setRestartHover(true)}
                onMouseLeave={() => setRestartHover(false)}
              >
                <RotateCcw size={18} style={commonStyles.iconMargin} /> Restart Game
            </button>
          </div>
        </>
      );
    } else {
      // Active playing screen (card display)
      return (
        <>
          <style>{globalStyleSheet}</style>
          <div style={appContainerStyle}>
            <header style={headerContainerStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h1 style={appTitleStyle}>Desi Monikers</h1>
            <div>
              <button
                style={rulesButtonStyle}
                onClick={() => setShowRules(true)}
                onMouseEnter={() => setRulesButtonHover(true)}
                onMouseLeave={() => setRulesButtonHover(false)}
                aria-label="Show Game Rules"
              >
                Rules
              </button>
              <button 
                style={helpButtonStyle}
                onClick={() => setShowHelp(!showHelp)}
                onMouseEnter={() => setHelpButtonHover(true)}
                onMouseLeave={() => setHelpButtonHover(false)}
                aria-label="Show Quick Help"
              >
                <HelpCircle size={24} />
              </button>
            </div>
          </div>
          {showHelp && (
            <div style={helpTextContainerStyle} className={showHelp ? "animate-enter-active" : ""}>
              <p><strong>Round 1:</strong> Describe freely (no name parts!).</p>
              <p><strong>Round 2:</strong> Use only ONE word.</p>
              <p><strong>Round 3:</strong> Charades only (no words).</p>
              <p style={{marginTop: '0.25rem'}}>Swipe right or click ✓ for CORRECT. Swipe left or click X to PASS.</p>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.25)', padding: '0.75rem', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                <div style={{ textAlign: 'center', color: currentTeam === 1 ? '#ec4899': 'white', fontWeight: currentTeam === 1 ? 700 : 500 }}>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team1Name}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team1Score}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Round {round}/3</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: timerActive && timer <=10 && timer % 2 === 0 ? '#ef4444' : 'white' }}>
                    {timerActive ? `${timer}s` : (isTurnOver ? "Time Up!" : "Paused")}
            </div>
            </div>
                <div style={{ textAlign: 'center', color: currentTeam === 2 ? '#ec4899': 'white', fontWeight: currentTeam === 2 ? 700 : 500 }}>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{team2Name}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{team2Score}</div>
                </div>
              </div>
               <div style={{marginTop: '0.5rem', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.875rem'}}>
                 {currentTeam === 1 ? team1Name : team2Name}'s Turn ({cardsRemainingInDeck} cards left in deck)
          </div>
        </header>

        <main style={mainGameAreaStyle}>
            <div 
              style={cardElementBaseStyle}
              className={animationClass}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {currentCard ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isDesktop ? '1rem' : '0.75rem' }}>
                    <h2 style={cardNameStyle}>{currentCard.name}</h2>
                    <span style={cardPointsStyle}>{currentCard.points} Pts</span>
                  </div>
                  <div style={clueContainerStyle}>
                    <p style={clueTextStyle}>
                      {round === 1 && currentCard.clue}
                      {round === 2 && "Describe using only ONE WORD!"}
                      {round === 3 && "ACT IT OUT! (Charades - No words)"}
                          {/* Removed the small start turn button here as it's handled by isGameStarting screen or nextTurn screen */}
                    </p>
                  </div>
                </>
              ) : (
                 <div style={{ ...cardElementBaseStyle, alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: '#f3f4f6' }}>
                    <ListChecks size={isDesktop ? 64 : 48} style={{color: '#10b981', marginBottom: '1rem'}} />
                    <h2 style={{fontSize: isDesktop ? '1.5rem' : '1.25rem', fontWeight: 700, color: '#374151'}}>
                      {deck.length === 0 && round > 1 ? `No cards for Round ${round}!` : "All cards played!"}
                    </h2>
                    <p style={{color: '#6b7280', marginTop: '0.5rem', fontSize: isDesktop ? '1rem' : '0.875rem'}}>
                      {deck.length === 0 && round > 1 ? "Previous round had no guessed cards." : "Round over."}
                    </p>
                </div>
              )}
            </div>
        </main>
        
            {!isRoundOver && !isTurnOver && currentCard && timerActive && (
          <footer style={{ width: '100%', maxWidth: '28rem', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem' }}>
            <button
              style={actionButtonStyle('danger', passHover)}
              onClick={() => handleNextCard('pass')}
                  disabled={isSwiping || animationClass.includes('swipe') || !currentCard || !timerActive || deck.length <=1}
              aria-label="Pass Card"
              onMouseEnter={() => setPassHover(true)}
              onMouseLeave={() => setPassHover(false)}
            >
              <X size={28} style={commonStyles.iconMargin} /> PASS
            </button>
            <button
              style={actionButtonStyle('success', correctHover)} // 'success' will map to green
              onClick={() => handleNextCard('correct')}
                  disabled={isSwiping || animationClass.includes('swipe') || !currentCard || !timerActive}
              aria-label="Correctly Guessed Card"
              onMouseEnter={() => setCorrectHover(true)}
              onMouseLeave={() => setCorrectHover(false)}
            >
              <Check size={28} style={commonStyles.iconMargin} /> CORRECT
            </button>
          </footer>
        )}
         <button 
            style={restartButtonStyle} 
            onClick={restartGame} 
            aria-label="Restart Game"
            onMouseEnter={() => setRestartHover(true)}
            onMouseLeave={() => setRestartHover(false)}
          >
            <RotateCcw size={18} style={commonStyles.iconMargin} /> Restart Game
        </button>

        {showRules && (
          <div style={rulesModalOverlayStyle}>
            <div style={{...rulesModalContentStyle, fontFamily: "'Inter', sans-serif"}} className="animate-enter-active">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: isDesktop ? '1.5rem' : '1.25rem', fontWeight: 700, color: '#374151' }}>Game Rules</h3>
                <button 
                  onClick={() => setShowRules(false)} 
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem', position: 'absolute', top: isDesktop? '0.75rem' : '0.5rem', right: isDesktop ? '0.75rem' : '0.5rem' }}
                  aria-label="Close Rules"
                >
                  <X size={isDesktop ? 28 : 24} />
                </button>
              </div>
              
              <h4 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.25rem', fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4f46e5' }}>Objective:</h4>
              <p style={{ marginBottom: '0.75rem', lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>The goal is to collectively guess as many names as possible over 3 distinct rounds, each with different clue-giving rules. Aim for a high score!</p>

              <h4 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.25rem', fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4f46e5' }}>Setup:</h4>
              <p style={{ marginBottom: '0.75rem', lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>Familiarize yourself with the cards: names of Indian personalities, movies, places, and cultural items. Round 1 begins with all cards from the initial set.</p>
              
              <h4 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.25rem', fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4f46e5' }}>Rounds (3 Rounds Total):</h4>
              <p style={{ marginBottom: '0.5rem', lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>
                The game is played over three rounds. Each round has unique rules for giving clues.
                Cards correctly guessed in a round become the deck for the <strong>next</strong> round. 
                Cards you choose to "PASS" are removed from the game and won't appear in subsequent rounds.
                If no cards are guessed in a round to carry over, the game ends early.
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '0.75rem', lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Round 1: Describe It!</strong>
                  <ul style={{ listStyle: 'circle', paddingLeft: '20px', marginTop: '0.25rem' }}>
                    <li>Give clues to help guess the name (the card's clue is a good starting point!).</li>
                    <li>You CANNOT say any part of the name on the card.</li>
                    <li>You CANNOT say "rhymes with" or "sounds like."</li>
                    <li>Otherwise, describe freely!</li>
                  </ul>
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Round 2: One Word Only!</strong>
                  <ul style={{ listStyle: 'circle', paddingLeft: '20px', marginTop: '0.25rem' }}>
                    <li>Use ONLY ONE WORD as a clue for the name.</li>
                    <li>This word cannot be any part of the name itself.</li>
                    <li>Gestures are NOT allowed.</li>
                  </ul>
                </li>
                <li>
                  <strong>Round 3: Act It Out! (Charades)</strong>
                  <ul style={{ listStyle: 'circle', paddingLeft: '20px', marginTop: '0.25rem' }}>
                    <li>Act out the name on the card.</li>
                    <li>NO words. NO sounds (or minimal, non-verbal sounds if agreed for fun).</li>
                    <li>Only gestures and actions.</li>
                  </ul>
                </li>
              </ul>

              <h4 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.25rem', fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4f46e5' }}>Gameplay (How to Play This Version):</h4>
              <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '0.75rem', lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>
                <li>For each card presented, give clues according to the current round's rules. Imagine you're helping others guess (or testing yourself!).</li>
                <li><strong>Correct Guess:</strong> If you (or your imaginary team) guess it, swipe Right or click the CORRECT button. The card is added to your guessed pile for the round, and you score points.</li>
                <li><strong>Pass/Skip:</strong> If you're stuck or want to skip, swipe Left or click the PASS button. The card is removed from the current round and the game.</li>
              </ul>

              <h4 style={{ fontWeight: 600, marginTop: '1rem', marginBottom: '0.25rem', fontSize: isDesktop ? '1.125rem' : '1rem', color: '#4f46e5' }}>Winning/Game End:</h4>
              <p style={{ lineHeight: 1.6, fontSize: isDesktop ? '0.9rem' : '0.875rem' }}>The game ends after 3 rounds are completed, or if a round starts with no cards (because none were successfully guessed in the previous round). Your total score reflects how many points you accumulated. Challenge yourself to beat your high score!</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
    }
  }
  // Fallback for undefined gamePhase, though setup should be default
  return <div>Loading or undefined game state...</div>;
}

export default DesiMonikers;
